<template>
  <div>
    <div
      v-if="!hideDropzone"
      :class="[
        'border border-dashed border-primary-400 dark:border-primary-900 rounded-lg bg-primary-50 dark:bg-primary-950/50 p-4 w-full flex-col justify-center items-center text-primary-600  text-center transition-all mb-3 cursor-pointer hover:scale-101',
        { 'scale-101' : active}
      ]"
    >
      <div @dragover="handleDragover" @dragleave="handleDragleave" @drop="handleDrop" @click="handleUploadClick">
        <input
          :id="inputId"
          ref="input"
          type="file"
          class="w-px h-px opacity-0 overflow-hidden absolute"
          multiple
          @change="handleInputChange"
        >

        <div class="pointer-events-none">
          <div v-if="icon" class="h-8 w-8 bg-primary-200 dark:bg-primary-900 dark:text-white rounded-lg flex items-center justify-center mb-3 mx-auto">
            <o-icon icon="upload" :size="18" />
          </div>
          <h5 class="font-semibold">
            {{ labelResolved }}
          </h5>
          <p class="text-sm">
            {{ infoResolved }}
          </p>
        </div>
      </div>
    </div>

    <input
      v-else
      :id="inputId"
      ref="input"
      type="file"
      class="w-px h-px opacity-0 overflow-hidden absolute"
      multiple
      @change="handleInputChange"
    >

    <div v-if="files.length" class="mt-5">
      <div v-for="item in files" :key="`${item.id}-${item.revision}`" class="mb-3">
        <uploading
          :file="item.file"
          :params="params"
          @remove="handleRemove(item.id)"
          @retry="handleRetry(item.id)"
          @status="(status) => handleStatus(item.id, status)"
          @update="handleUpdate(item.id)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount, useId } from 'vue'
import { useNuxtApp } from '#app'
import debounce from 'lodash/debounce'
import Uploading from './Uploading.vue'

const { $i18n } = useNuxtApp()

interface Props {
  label?: string
  info?: string
  icon?: boolean
  hideDropzone?: boolean
  queueAutoClear?: boolean
  params?: Record<string, any>
}

const props = withDefaults(defineProps<Props>(), {
  label: undefined,
  info: undefined,
  icon: false,
  hideDropzone: false,
  queueAutoClear: false,
  params: () => ({}),
})

const emit = defineEmits<{
  (e: 'update'): void
}>()

// Handle default label/info that need $i18n
const labelResolved = computed(() => props.label ?? $i18n.t('components.upload.elements.upload.label'))
const infoResolved = computed(() => props.info ?? $i18n.t('components.upload.elements.upload.info'))

const input = ref<HTMLInputElement>()
const inputId = useId()
const active = ref(false)
const files = ref<Array<{ id: string, file: File, revision: number, status: string }>>([])
const clearTimers = new Map<string, ReturnType<typeof setTimeout>>()

function addFiles (fileList: FileList | File[]) {
  const nextFiles = Array.from(fileList)

  files.value.push(...nextFiles.map(file => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    file,
    revision: 0,
    status: 'queued',
  })))

  if (input.value) {
    input.value.value = ''
  }
}

function handleInputChange () {
  if (input.value?.files) {
    addFiles(input.value.files)
  }
}

function handleUploadClick () {
  input.value!.click()
}

function handleDragover ($event: DragEvent) {
  $event.preventDefault()

  active.value = true
}

function handleDragleave () {
  active.value = false
}

function handleDrop ($event: DragEvent) {
  $event.preventDefault()
  addFiles($event.dataTransfer!.files)

  active.value = false
}

const handleUpdate = debounce((id: string) => {
  handleStatus(id, 'complete')
  emit('update')
}, 500)

function handleRemove (id: string) {
  const timer = clearTimers.get(id)

  if (timer) {
    clearTimeout(timer)
    clearTimers.delete(id)
  }

  files.value = files.value.filter(item => item.id !== id)
}

function handleRetry (id: string) {
  const item = files.value.find(file => file.id === id)

  if (item) {
    item.revision += 1
    item.status = 'queued'
  }
}

function handleStatus (id: string, status: string) {
  const item = files.value.find(file => file.id === id)

  if (!item) return

  item.status = status

  if (props.queueAutoClear && status === 'complete' && !clearTimers.has(id)) {
    const timer = setTimeout(() => handleRemove(id), 5000)
    clearTimers.set(id, timer)
  }
}

onBeforeUnmount(() => {
  clearTimers.forEach(timer => clearTimeout(timer))
  clearTimers.clear()
})

defineExpose({
  open: handleUploadClick,
  addFiles,
})
</script>
